import { html, css } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { dom, List, DropdownList, Overlay } from "@omegagrid/core";
import { TreeDropdown, TreeEvent } from "@omegagrid/tree";
import rp, { AppComponent, GridEditor } from "@reporting/rp-app-host";
import { Accounting, AccountingRights } from "@reporting/rp-types/core";
import { SecurityRoleGridEditorAdapter } from "@reporting/rp-mf-definition"
import { msg } from "@omegagrid/localize";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { AdminApplication } from "..";
import { ModuleDefinition } from "@reporting/rp-types/definition";

const MIN_ITEM_HEIGHT = 112;

@customElement('rp-admin-securityroles-container')
export class SecurityRolesContainer extends AppComponent<AdminApplication> {

	static styles = css`
		:host {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: 100%;
			position: relative;
			min-height: 0;
		}

		#controls {
			display: flex;
			flex-direction: row;
			align-items: center;
			flex: 0;
			border-bottom: 1px solid var(--og-border-color);
			position: relative;
		}

		#controls * {
			height: 24px;
			margin: 4px 2px;
		}

		.body {
			flex: 1;
			overflow: auto;
			min-height: 0;
		}

		og-list {
			height: 100%;
		}
	`;

	accountings: Accounting[];

	@property({type: Array})
	renderedAccountings: Accounting[] = [];

	@property({type: Object})
	module: ModuleCode = 'F';

	@property({type: Array})
	groupModules: ModuleDefinition[] = [];

	@query('og-overlay')
	loader: Overlay;

	@query('og-tree-dropdown#accountings')
	accountingDropdown: TreeDropdown;

	@query('og-dropdown-list#modules')
	moduleDropdown: DropdownList;

	@query('og-list')
	list: List;

	async connectedCallback() {
		super.connectedCallback();
		await this.updateComplete;
		this.loader.show(this);
		const [admin, definition] = await Promise.all([
			rp.ms('accounting').get<Accounting[]>(
				`accountings/${rp.accountingProvider.accounting.id_accounting_group}`,
				{ right: AccountingRights.Admin }
			),
			rp.ms('accounting').get<Accounting[]>(
				`accountings/${rp.accountingProvider.accounting.id_accounting_group}`,
				{ right: AccountingRights.Definition }
			),
		]);
		const seen = new Set<number>();
		this.renderedAccountings = this.accountings = [...admin, ...definition]
			.filter(a => !seen.has(a.accounting) && seen.add(a.accounting))
			.sort((a, b) => a.accounting - b.accounting);
		this.accountingDropdown.setSourceData(
			this.accountings.map(a => ({ k: a.accounting.toString(), v: `${a.accounting} ${a.company}` })),
			{ search: true }
		);
		this.groupModules = await rp.ms('definition').get<ModuleDefinition[]>('groupModules');
		this.moduleDropdown.value = this.module = this.groupModules[0]?.module as ModuleCode || null;
		this.loader.hide();
	}

	getModules() {
		return this.groupModules?.map(m => ({ key: m.module, value: m.name }));
	}

	_onAccountingChange = (e: TreeEvent) => {
		this.renderedAccountings = e.node
			? this.accountings.filter(a => this.accountingDropdown.value.map(v => v[0]).includes(a.accounting.toString()))
			: this.accountings;
		this.list.requestUpdate();
	};

	_onModuleChange = () => {
		this.module = this.moduleDropdown.value as ModuleCode;
		this.list.requestUpdate();
	};

	_resizeGridEditor = (component: GridEditor) => {
		const height = component.grid.model.rows.reduce((acc, row) => acc + row.height, 0) + 90;
		dom.setSize(component, {h: Math.max(height, MIN_ITEM_HEIGHT)});
		component.layout();
	};

	_renderSecurityGridEditor = (div: HTMLDivElement, index: number) => {
		const accounting = this.renderedAccountings[index];
		const component = dom.createElement<GridEditor>('rp-editor-grid');
		component.adapter = new SecurityRoleGridEditorAdapter(this.module, accounting.accounting);
		component.style.minHeight = dom.px(MIN_ITEM_HEIGHT);
		component.style.height = '100%';
		component.style.marginRight = '20px';
		component.addEventListener('grid.render', () => this._resizeGridEditor(component), {once: true});
		component.addEventListener('edit', () => this._resizeGridEditor(component));
		component.addEventListener('save', () => this._resizeGridEditor(component));
		div.innerHTML = `<span style="font-weight: bold;">${accounting.accounting} ${accounting.company}</span>`;
		div.style.paddingTop = div.style.paddingBottom = '5px';
		div.appendChild(component);
	};

	layout() {}

	render = () => html`
		<og-overlay defaultLoader></og-overlay>
		<div id="controls">
			<og-tree-dropdown
				cross
				.placeholder="${msg('Company')}"
				id="accountings"
				style="width: 200px;"
				@change="${this._onAccountingChange}">
			</og-tree-dropdown>
			<og-dropdown-list
				id="modules"
				style="width: 100px;"
				.items="${this.getModules()}"
				@change="${this._onModuleChange}">
			</og-dropdown-list>
		</div>
		<div class="body">
			<og-list
				disabledSelect
				dynamicItemHeight
				nativeHeight
				.itemRenderer="${this._renderSecurityGridEditor}"
				.size="${this.renderedAccountings.length}">
			</og-list>
		</div>
	`;

}
